import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQ = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch FAQ from API
  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        // Sort by order field
        const sorted = response.data.sort((a, b) => (a.order || 0) - (b.order || 0));
        setFaqItems(sorted);
      } catch (error) {
        console.error("Error fetching FAQ:", error);
        toast.error("Erreur lors du chargement de la FAQ");
      } finally {
        setLoading(false);
      }
    };
    fetchFaq();
  }, []);

  return (
    <div data-testid="faq-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            FAQ
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            Questions fréquentes
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            Tout ce que vous devez savoir avant, pendant et après votre tatouage.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <div className="max-w-3xl">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-secondary animate-pulse rounded" />
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible className="space-y-2">
                {faqItems.map((item, idx) => (
                  <AccordionItem
                    key={item.id || idx}
                    value={item.id || `faq-${idx}`}
                    className="border border-border/40 px-6"
                    data-testid={`faq-item-${idx}`}
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="text-base font-medium pr-4">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground font-light pb-5 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-border/40 bg-secondary/30">
        <div className="page-container text-center">
          <h2 className="text-2xl font-heading mb-4">
            Vous avez d'autres questions ?
          </h2>
          <p className="text-muted-foreground font-light mb-8">
            N'hésitez pas à me contacter, je serai ravie de vous répondre.
          </p>
          <Link to="/contact" data-testid="faq-contact-link">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs">
              Me contacter
              <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
